import type { SupSingleVaultV3, SupWeb3VaultBalance } from "@/types";
import type { AppAdminConfig } from "@/hooks/api/useGetAdminConfig";
import type { SupRecordUnsupportedReason } from "@/components/Ui/Tooltip/Unsupported";

import { useMemo } from "react";

import { checkWithdrawalStored, useStoredData } from "@/store";

/**
 * Logic behind the withdrawal button's state and tooltip
 *
 * @param {SupSingleVaultV3} singleVault
 * @param {SupWeb3VaultBalance} vaultBalance
 * @param {AppAdminConfig | undefined} adminConfig
 * @returns {{reason: SupRecordUnsupportedReason; isDisabled: boolean; isCompleted: boolean}}
 */
export default function useWithdrawalButton(
  singleVault: SupSingleVaultV3,
  vaultBalance?: SupWeb3VaultBalance,
  adminConfig: AppAdminConfig | undefined = undefined
): { reason: SupRecordUnsupportedReason; isDisabled: boolean; isCompleted: boolean } {
  const storedData = useStoredData();

  const { areAllWithdrawalsDisabled, withdrawalCartLimit, areAllDisabled, positionsChainDisabled } = useMemo(() => {
    const restrictedPositionChains = adminConfig?.rawConfig.exclude_superposition_chains_withdrawals ?? [];
    const restrictedVaultChains = adminConfig?.rawConfig.exclude_from_chains_withdrawals ?? [];
    return {
      areAllDisabled: (adminConfig?.isWithdrawalDisabled && adminConfig?.isDepositDisabled) ?? false,
      withdrawalCartLimit: adminConfig?.withdrawalCartLimit ?? 1,
      areAllWithdrawalsDisabled: adminConfig?.isWithdrawalDisabled ?? false,
      positionsChainDisabled: (!!vaultBalance && restrictedPositionChains.includes(vaultBalance.chain_id as number)) || restrictedVaultChains.includes(singleVault.chain.id),
    };
  }, [
    adminConfig?.isDepositDisabled,
    adminConfig?.isWithdrawalDisabled,
    adminConfig?.rawConfig.exclude_from_chains_withdrawals,
    adminConfig?.rawConfig.exclude_superposition_chains_withdrawals,
    adminConfig?.withdrawalCartLimit,
    singleVault.chain.id,
    vaultBalance,
  ]);

  return useMemo(() => {
    const isWithdrawWrongChain = !!storedData.withdrawalChainId && !!vaultBalance && vaultBalance.chain_id !== storedData.withdrawalChainId;
    const isWithdrawLimit = storedData.withdrawCart.length >= withdrawalCartLimit;
    const isWithdrawUnavailable = ["unavailable", "paused", "deposit_only"].includes(singleVault.availability);
    const isWithdrawUnavailableReason = isWithdrawUnavailable ? (["unavailable", "paused"].includes(singleVault.availability) ? "unavailable" : "withdrawUnavailable") : undefined;
    const isInWithdrawalCart = checkWithdrawalStored(storedData, singleVault.id, vaultBalance?.chain_id, vaultBalance?.is_erc20);

    return {
      isWithdrawWrongChain,
      isDisabled: (isWithdrawLimit && !isInWithdrawalCart) || isWithdrawUnavailable || areAllWithdrawalsDisabled || isWithdrawWrongChain || positionsChainDisabled,
      isCompleted: isInWithdrawalCart,
      reason: (areAllDisabled
        ? "disabled"
        : isWithdrawUnavailable
        ? "unavailable"
        : isWithdrawUnavailableReason
        ? "disabledChain"
        : isWithdrawWrongChain
        ? "withdraw"
        : isWithdrawLimit
        ? "withdrawLimit"
        : areAllWithdrawalsDisabled
        ? "disabledWithdrawal"
        : undefined) as SupRecordUnsupportedReason,
    };
  }, [storedData, vaultBalance, withdrawalCartLimit, singleVault.availability, singleVault.id, areAllWithdrawalsDisabled, positionsChainDisabled, areAllDisabled]);
}
